import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { useNavigate, useLocation } from 'react-router';
import { usePostHog } from '@posthog/react';
import { authActions } from '../../actions/auth';
import authSlice from '../../utils/redux/slice/authSlice';
import './AuthScreen.css';

function VerifyEmailScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const initialEmail = location.state?.email || '';
  const [form, setForm] = useState({ email: initialEmail, otp: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { email, otp } = form;
    try {
      const result = await authActions.verifyEmail(email, otp);
      dispatch(authSlice.actions.setUser(result.user));
      dispatch(authSlice.actions.setToken(result.token));
      dispatch(authSlice.actions.setRefreshToken(result.refreshToken));
      dispatch(authSlice.actions.setPermissions(result.permissions));
      posthog?.identify(result.user?.client_id || result.user?.client_email, {
        email: result.user?.client_email,
        name: result.user?.client_name,
      });
      posthog?.capture('user_signed_up', { method: 'email' });
      message.success('Email verified successfully');
      navigate('/');
    } catch (error) {
      message.error(error?.response?.data?.error || error?.response?.data?.message || 'Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authActions.resendVerification(form.email);
      setResendSuccess(true);
      setResendCooldown(60);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to resend code. Please try again.';
      message.error(errorMessage);
    }
  };

  return (
    <div className="auth-container">
      {[...Array(6)].map((_, i) => (
        <span key={i} className="bubble" />
      ))}
      <div className="auth-card">
        <div className="logo-container">
          <img src="/uro_logo_white.png" alt="URO" className="logo" />
        </div>
        <h2>Verify Email</h2>
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="verifyEmail">Email</label>
            <input
              id="verifyEmail"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="otp">OTP</label>
            <input
              id="otp"
              type="text"
              value={form.otp}
              onChange={(e) => setForm({ ...form, otp: e.target.value })}
              required
              maxLength={6}
            />
          </div>
          <button
            className="button"
            type="submit"
            disabled={isSubmitting}
            style={{ opacity: isSubmitting ? 0.65 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
          >
            {isSubmitting ? 'Verifying…' : 'Verify'}
          </button>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              style={{
                opacity: resendCooldown > 0 ? 0.5 : 1,
                cursor: resendCooldown > 0 ? 'default' : 'pointer',
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                fontSize: 13,
                textDecoration: 'underline',
              }}
            >
              {resendSuccess
                ? 'Code sent!'
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend code'}
            </button>
          </div>
          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/login')}
          >
            Back to login
          </button>
        </form>
      </div>
    </div>
  );
}

export default VerifyEmailScreen;
